import { MouseEvent, ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import clsx from "clsx";

import { GlobalState } from "@app/store";
import PackageService from "@app/services/http/package.service";
import { Category, Package } from "@app/types";
import useObservable from "@core/hooks/use-observable.hook";
import useOnClickOutside from "@core/hooks/use-on-click-outside.hook";
import { bySortOrder, formatMoneyVN } from "@app/utils/util";
import { PathnameUrl } from "@app/constants";
import { Lang, ThemePref, usePreferences } from "@app/contexts/preferences.context";
import { LoadingBlock, PcGlyph } from "@app/components/ui";
import {
  IcActivity,
  IcCheck,
  IcChevronRight,
  IcClock,
  IcCpu,
  IcDiscord,
  IcFacebook,
  IcFileText,
  IcFilter,
  IcGamepad,
  IcGlobe,
  IcIdCard,
  IcInfo,
  IcMail,
  IcMapPin,
  IcMonitor,
  IcMonitorSmartphone,
  IcMoon,
  IcPhone,
  IcReceipt,
  IcServer,
  IcSun,
  IcWallet,
  IcYoutube,
} from "@app/components/icons";
import { TONE_TAG, Tone, categoryTones } from "../gpu-hub/category-tone";

/*
 * Trang chủ theo bản thiết kế "PCRender Trang chủ".
 * Mọi tiêu đề (H1/H2/H3/H4) và đoạn văn giữ đúng chữ và cấp tiêu đề của bản thiết kế
 * (giữ chữ của pcrender.com/home cũ + từ khoá SEO đã chốt), đừng đổi khi sửa giao diện.
 * Chữ tiếng Việt là khoá dịch: bản tiếng Anh nằm trong i18n/en.ts, mục "trang chủ".
 * Bảng giá dùng đúng nguồn cũ: GET /packages (không cần đăng nhập).
 */

const FAQ: [string, string, string?][] = [
  [
    "Cho thuê máy tính từ xa là gì?",
    "Bạn thuê một máy tính vật lý cấu hình cao đặt tại PCRender và điều khiển từ xa qua Remote Desktop hoặc PCRender Play. Máy được tạo tự động ngay sau khi thanh toán, dùng theo giờ, ngày hoặc tháng.",
  ],
  [
    "Thuê máy render Blender, Solidworks nên chọn cấu hình nào?",
    "Render video và Blender nên chọn máy có RTX 3090 hoặc 4090 VRAM 24GB. Thuê PC chạy Solidworks nên ưu tiên CPU xung cao như i7, i9 và RAM từ 64GB. Xem các cấu hình Render trong bảng giá phía trên.",
    // câu cuối chỉ hiện trên trang, không đưa vào dữ liệu có cấu trúc (như bản thiết kế)
    "Render video và Blender nên chọn máy có RTX 3090 hoặc 4090 VRAM 24GB. Thuê PC chạy Solidworks nên ưu tiên CPU xung cao như i7, i9 và RAM từ 64GB.",
  ],
  [
    "Có thuê PC treo giả lập LDPlayer theo giờ được không?",
    "Được. Các cấu hình Giả lập dùng CPU dual Xeon và RAM 128GB đến 256GB, treo game giả lập LDPlayer nhiều cửa sổ, thuê từ 1 giờ và gia hạn bất cứ lúc nào.",
  ],
  [
    "Thuê server GPU và thuê VPS GPU khác nhau thế nào?",
    "Thuê server GPU là thuê nguyên một máy chủ vật lý, toàn bộ CPU và card đồ họa dành riêng cho bạn. Thuê VPS GPU là máy ảo chia sẻ phần cứng, giá rẻ hơn, phù hợp tác vụ nhẹ và chạy lâu dài theo tháng.",
  ],
  [
    "Thuê máy chơi Cloud Gaming tính tiền thế nào?",
    "Bạn mua gói gồm số giờ chơi và hạn sử dụng. Giờ chỉ bị trừ khi máy đang bật, mỗi lần bật có giới hạn session và máy tự tắt khi hết session.",
  ],
];

const OS_LIST: [string, string, "win" | "linux"][] = [
  ["Windows 10 Pro", "Thuê máy, Cloud", "win"],
  ["Windows 11 Pro", "Thuê máy, Cloud", "win"],
  ["Windows Server 2022", "Thuê máy", "win"],
  ["Ubuntu", "Linux · Thuê VPS", "linux"],
];

const THEMES: [ThemePref, ReactNode, string][] = [
  ["light", <IcSun key="l" />, "Sáng"],
  ["dark", <IcMoon key="d" />, "Tối"],
  ["system", <IcMonitorSmartphone key="s" />, "Hệ thống"],
];

const LANGS: [Lang, string][] = [
  ["vi", "Tiếng Việt"],
  ["en", "English"],
];

const ALL = "";
const MOBILE_ROWS = 6;

// GET /packages trả category đã populate ({ _id, name }) dù type Package ghi là string
const categoryOf = (p: Package) =>
  typeof p.category === "object" && p.category
    ? (p.category as unknown as Pick<Category, "_id" | "name" | "sort_order">)
    : undefined;

const RE_RENDER = /render/i;
const RE_GIALAP = /giả lập|gia lap/i;

function OsLogo({ kind }: { kind: "win" | "linux" }) {
  return kind === "win" ? (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#1A7FE8" d="M3 5.5 10.5 4.5v7H3zM11.5 4.4 21 3v8.5h-9.5zM3 12.5h7.5v7L3 18.5zM11.5 12.5H21V21l-9.5-1.4z" />
    </svg>
  ) : (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="9" fill="#E95420" />
      <circle cx="12" cy="12" r="4.2" fill="none" stroke="#fff" strokeWidth="1.8" />
      <circle cx="5.6" cy="12" r="1.7" fill="#fff" />
      <circle cx="15.2" cy="6.5" r="1.7" fill="#fff" />
      <circle cx="15.2" cy="17.5" r="1.7" fill="#fff" />
    </svg>
  );
}

function Home() {
  const { myInfo } = useSelector(selectMyInfo);
  const { t, theme, setTheme, lang, setLang, isDark } = usePreferences();
  const { subscribeUntilDestroy } = useObservable();

  const [packages, setPackages] = useState<Package[]>([]);
  // chưa có dữ liệu lần đầu: hiện "Đang tải…" thay cho "Không có cấu hình nào"
  const [loaded, setLoaded] = useState(false);
  const [cat, setCat] = useState(ALL);
  const [more, setMore] = useState(false);
  const [pop, setPop] = useState<"lang" | "theme" | null>(null);
  const popRef = useRef<HTMLDivElement>(null);

  useOnClickOutside(popRef, () => setPop(null));

  useEffect(() => {
    subscribeUntilDestroy(PackageService.getAllPackages(), (res) => {
      setPackages(res?.length ? res : []);
      setLoaded(true);
    });
  }, [subscribeUntilDestroy]);

  // JSON-LD theo đúng ngôn ngữ đang hiện trên trang
  const faqLd = useMemo(
    () =>
      JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: FAQ.map(([q, a, ld]) => ({
          "@type": "Question",
          name: t(q),
          acceptedAnswer: { "@type": "Answer", text: t(ld || a) },
        })),
      }),
    [t]
  );

  const loggedIn = !!myInfo._id;
  // Đã đăng nhập thì vào thẳng trang thuê, chưa thì sang Đăng ký như trang cũ
  const rentTo = loggedIn ? PathnameUrl.GPU_HUB : "/register";
  const pageTo = (path: string) => (loggedIn ? path : "/login");

  const categories = useMemo(() => {
    const seen: Record<string, Pick<Category, "_id" | "name" | "sort_order">> = {};

    packages.forEach((p) => {
      const c = categoryOf(p);

      if (c && !seen[c._id]) seen[c._id] = c;
    });

    // chip danh mục theo thứ tự admin nhập như trang Thuê máy tính
    return Object.values(seen).sort(bySortOrder);
  }, [packages]);

  const tones = useMemo(() => categoryTones(categories), [categories]);
  const toneOf = (p: Package): Tone | undefined => {
    const c = categoryOf(p);

    return c ? tones[c._id] : undefined;
  };

  const minHour = (list: Package[]) => (list.length ? Math.min(...list.map((p) => p.price_per_hour || 0)) : 0);
  const fromText = (re?: RegExp) => {
    const list = re ? packages.filter((p) => re.test(categoryOf(p)?.name || "")) : packages;

    return list.length ? (
      <>
        {list.length} {t("cấu hình")} · {t("từ")}{" "}
        <b className="num">
          {formatMoneyVN(minHour(list))} {t("VND/giờ")}
        </b>
      </>
    ) : null;
  };

  const rows = cat === ALL ? packages : packages.filter((p) => categoryOf(p)?._id === cat);
  const mobileRows = more ? rows : rows.slice(0, MOBILE_ROWS);

  const scrollToPrice = (e: MouseEvent) => {
    e.preventDefault();
    document.getElementById("bang-gia")?.scrollIntoView({
      behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
      block: "start",
    });
  };

  const tag = (p: Package) => {
    const c = categoryOf(p);
    const tone = toneOf(p);

    return c ? (
      <div className="tags">
        <span className={clsx("tag", tone && TONE_TAG[tone])}>{c.name}</span>
      </div>
    ) : null;
  };

  const gpu = (p: Package) => (p.gpu ? p.gpu : <span className="novga">{t("Không có VGA")}</span>);
  const money = (n?: number) => (n ? formatMoneyVN(n) : "");
  const rentBtn = (
    <Link className="btn btn-primary btn-sm" to={rentTo}>
      {t("Thuê Ngay")}
    </Link>
  );

  const popItem =
    "flex w-full items-center gap-2.5 rounded-[7px] px-2.5 py-2 text-left text-[13px] text-fg hover:bg-surface-2";
  const popBox =
    "absolute right-0 top-[46px] z-[60] min-w-[180px] rounded-[10px] border border-line bg-surface p-1.5 shadow-[0_10px_30px_rgba(0,0,0,.18)]";

  return (
    <div className="hp-page min-h-screen bg-bg text-fg">
      <Helmet>
        {/* thẻ description nằm sẵn trong public/index.html (nội dung theo bản thiết kế), không thêm thẻ thứ 2 */}
        <script type="application/ld+json">{faqLd}</script>
      </Helmet>

      {/* ---------- đầu trang: logo, ngôn ngữ, giao diện, Đăng nhập / Đăng ký ---------- */}
      <div className="hp-topwrap">
        <header className="mx-auto flex h-16 w-full max-w-[1290px] items-center gap-3 px-6 max-[900px]:px-4 max-[520px]:gap-2">
          <Link className="logo hp-logo" to="/home" aria-label={t("PC Render trang chủ")}>
            <span>PC</span>Render
          </Link>
          <span className="flex-1" />
          <div ref={popRef} className="flex items-center gap-3 max-[520px]:gap-2">
            <div className="relative">
              <button
                type="button"
                className="btn btn-ghost lang-btn !h-[38px] !px-2.5"
                aria-label={t("Ngôn ngữ")}
                title={t("Ngôn ngữ")}
                aria-haspopup="menu"
                aria-expanded={pop === "lang"}
                onClick={() => setPop(pop === "lang" ? null : "lang")}
              >
                <IcGlobe className="i !h-4 !w-4" />
                <span className="max-[360px]:hidden">{lang.toUpperCase()}</span>
              </button>
              {pop === "lang" && (
                <div className={popBox}>
                  {LANGS.map(([key, label]) => (
                    <button
                      key={key}
                      type="button"
                      className={popItem}
                      onClick={() => {
                        setLang(key);
                        setPop(null);
                      }}
                    >
                      <span className="w-[22px] text-[11px] font-bold text-muted">{key.toUpperCase()}</span>
                      <span lang={key}>{label}</span>
                      {lang === key && <IcCheck className="ml-auto text-brand" />}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="relative">
              <button
                type="button"
                className="iconbtn text-[18px]"
                aria-label={t("Giao diện")}
                title={t("Giao diện")}
                aria-haspopup="menu"
                aria-expanded={pop === "theme"}
                onClick={() => setPop(pop === "theme" ? null : "theme")}
              >
                {theme === "system" ? <IcMonitorSmartphone /> : isDark ? <IcMoon /> : <IcSun />}
              </button>
              {pop === "theme" && (
                <div className={popBox}>
                  {THEMES.map(([key, icon, label]) => (
                    <button
                      key={key}
                      type="button"
                      className={popItem}
                      onClick={() => {
                        setTheme(key);
                        setPop(null);
                      }}
                    >
                      <span className="text-[16px]">{icon}</span>
                      {t(label)}
                      {theme === key && <IcCheck className="ml-auto text-brand" />}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          {loggedIn ? (
            <Link className="btn btn-primary hp-login" to={PathnameUrl.GPU_HUB}>
              {t("Trang quản lý")}
            </Link>
          ) : (
            <>
              <Link className="btn btn-ghost hp-login ghost" to="/login">
                {t("Đăng nhập")}
              </Link>
              <Link className="btn btn-primary hp-login" to="/register">
                {t("Đăng ký")}
              </Link>
            </>
          )}
        </header>
      </div>

      <main className="px-6 pb-12 pt-6 max-[900px]:px-4 max-[760px]:pt-3.5">
        <div className="hp-wrap">
          {/* ---------- hero ---------- */}
          <section className="hp-hero hp-sec" aria-labelledby="hp-t">
            <div className="hp-hero-txt">
              <span className="hp-eyebrow">{t("Cho thuê máy tính từ xa")}</span>
              <h2 id="hp-t">
                PC<em>Render</em>
              </h2>
              <p className="hp-lead">
                {t("Cho thuê máy tính vật lý sử dụng CPU")} <b>{t("dual Xeon lên tới 44 core 88 luồng")}</b>{" "}
                {t("kết hợp")} <b>{t("VGA 3060 12GB")}</b> {t("vô cùng mạnh mẽ chỉ từ")} <b>{t("15.000 vnđ")}</b>{" "}
                {t("sử dụng ngay.")}
              </p>
              <p className="hp-kw">
                {t(
                  "Cho thuê PC cấu hình cao, thuê server GPU và thuê VPS GPU cho render, đồ họa, AI, treo giả lập và Cloud Gaming."
                )}
              </p>
              <div className="hp-sub">
                <span>{t("Website cho thuê tự động 24/7.")}</span>
                <span>{t("Đăng ký, đăng nhập để sử dụng tính năng!")}</span>
              </div>
              <div className="hp-cta">
                <Link className="btn btn-primary" to={rentTo}>
                  <IcMonitor className="i" />
                  {t("Thuê máy ngay")}
                </Link>
                <a className="btn btn-ghost" href="#bang-gia" onClick={scrollToPrice}>
                  <IcReceipt className="i" />
                  {t("Xem bảng giá")}
                </a>
              </div>
            </div>
            <div className="hp-art" aria-hidden="true">
              <div className="plate" />
              <div className="rig">
                <PcGlyph height={150} color="var(--c-gl)" />
                <PcGlyph height={200} color="var(--brand)" />
                <PcGlyph height={150} color="var(--c-rd)" />
              </div>
              <div className="hp-float hp-f1">
                <span className="ib bg-brand-soft text-brand">
                  <IcCpu className="i" />
                </span>
                <div>
                  Dual Xeon<small>{t("44 core · 88 luồng")}</small>
                </div>
              </div>
              <div className="hp-float hp-f2">
                <span className="ib bg-c-gm-soft text-c-gm">
                  <IcMonitor className="i" />
                </span>
                <div>
                  VGA 3060 12GB<small>{t("tới RTX 4090")}</small>
                </div>
              </div>
              <div className="hp-float hp-f3">
                <span className="ib bg-ok-soft text-ok">
                  <IcClock className="i" />
                </span>
                <div>
                  {t("Tự động 24/7")}
                  <small>{t("tạo máy ngay sau khi thanh toán")}</small>
                </div>
              </div>
            </div>
          </section>

          {/* ---------- số liệu ---------- */}
          <div className="hp-stats" aria-label={t("PC Render trong số liệu")}>
            {(
              [
                [<IcServer key="s" className="i" />, loaded ? packages.filter((p) => !p.cloud_pc).length : "…", "cấu hình máy vật lý"],
                [<IcMonitor key="m" className="i" />, loaded ? formatMoneyVN(minHour(packages)) : "…", "VND/giờ, giá thấp nhất"],
                [<IcClock key="c" className="i" />, "24/7", "tự động tạo máy"],
                [<IcWallet key="w" className="i" />, t("Giờ · Ngày · Tháng"), "thanh toán linh hoạt"],
              ] as [ReactNode, ReactNode, string][]
            ).map(([icon, value, label]) => (
              <div key={label} className="hp-stat">
                <span className="ib">{icon}</span>
                <div>
                  <b className="num">{value}</b>
                  <span>{t(label)}</span>
                </div>
              </div>
            ))}
          </div>

          {/* ---------- dịch vụ tiêu biểu ---------- */}
          <section className="hp-sec" aria-labelledby="hp-svc-t">
            <div className="hp-head">
              <h2 id="hp-svc-t">{t("Dịch Vụ Tiêu Biểu Tại PcRender")}</h2>
              <p>{t("Dịch vụ Cloud VPS, hiệu suất cao, đồng hành cùng sự phát triển của bạn.")}</p>
            </div>
            <div className="hp-svc">
              <Link className="hp-card" to={rentTo} style={{ ["--glow" as string]: "var(--c-rd)" }}>
                <div className="vis">
                  <PcGlyph height={92} color="var(--c-rd)" />
                </div>
                <h4>PC RENDER</h4>
                <p>{t("PC vật lý chạy trên hệ điều hành Windows. Hỗ trợ VGA 4080, 4090…")}</p>
                <p>{t("Máy tính render video, thuê máy render Blender, thuê PC chạy Solidworks theo giờ.")}</p>
                <span className="from">{fromText(RE_RENDER)}</span>
                <span className="go">
                  {t("Xem cấu hình Render")}
                  <IcChevronRight className="i" />
                </span>
              </Link>
              <Link className="hp-card" to={rentTo} style={{ ["--glow" as string]: "var(--brand)" }}>
                <div className="vis">
                  <div className="flex items-end gap-1">
                    <PcGlyph height={70} color="var(--c-gl)" />
                    <PcGlyph height={92} color="var(--brand)" />
                    <PcGlyph height={70} color="var(--c-rd)" />
                  </div>
                </div>
                <h4>{t("Dịch vụ Thuê Máy Chủ Vật Lý")}</h4>
                <p>
                  {t(
                    "Dịch vụ Thuê Server tại Pc Render Cloud có Phần cứng máy chủ mạnh mẽ, tốc độ mạng nhanh, hiệu năng cao, hỗ trợ 24/7."
                  )}
                </p>
                <p>{t("Thuê server GPU chạy render, AI và đồ họa, trả theo giờ, ngày hoặc tháng.")}</p>
                <span className="from">{fromText()}</span>
                <span className="go">
                  {t("Xem tất cả máy chủ")}
                  <IcChevronRight className="i" />
                </span>
              </Link>
              <Link className="hp-card" to={rentTo} style={{ ["--glow" as string]: "var(--c-gl)" }}>
                <div className="vis">
                  <PcGlyph height={92} color="var(--c-gl)" />
                </div>
                <h4>{t("PC GIẢ LẬP")}</h4>
                <p>
                  {t("PC vật lý chạy trên hệ điều hành Windows. Hỗ trợ CPU Xeon 2680V4, 2696V4… cùng VGA 1080, 1080Ti, 3060…")}
                </p>
                <p>{t("Thuê PC treo giả lập, treo game giả lập LDPlayer nhiều cửa sổ, chạy MMO 24/7.")}</p>
                <span className="from">{fromText(RE_GIALAP)}</span>
                <span className="go">
                  {t("Xem cấu hình Giả lập")}
                  <IcChevronRight className="i" />
                </span>
              </Link>
            </div>
            <div className="hp-more mt-3.5">
              <Link to={pageTo(PathnameUrl.CLOUD_RENTAL)}>
                <IcGamepad className="i" />
                {t("Cloud Gaming - Render")}
              </Link>
              <Link to={pageTo(PathnameUrl.VPS_RENTAL)}>
                <IcServer className="i" />
                {t("Thuê VPS")}
              </Link>
              <Link to={pageTo(PathnameUrl.PC_COMPONENT)}>
                <IcCpu className="i" />
                {t("Thuê linh kiện mang về")}
              </Link>
            </div>
          </section>

          {/* ---------- bảng giá (GET /packages như trang cũ) ---------- */}
          <section className="panel hp-price hp-sec" id="bang-gia" aria-labelledby="hp-price-t">
            <div className="panel-head">
              <div>
                <h1 id="hp-price-t" className="text-[20px] font-bold">
                  {t("Bảng Giá Dịch Vụ Thuê Máy Chủ Vật Lý")}
                </h1>
                <p>{loaded ? `${packages.length} ${t("cấu hình")} · ${t("đơn vị VNĐ")}` : t("Đơn vị VNĐ")}</p>
              </div>
            </div>
            {categories.length > 0 && (
              <div className="hp-ptools">
                <div className="chips" role="group" aria-label={t("Lọc theo nhu cầu")}>
                  {[{ _id: ALL, name: t("Tất cả") }, ...categories].map((c) => (
                    <button
                      key={c._id || "all"}
                      type="button"
                      className="chip"
                      aria-pressed={cat === c._id}
                      onClick={() => {
                        setCat(c._id);
                        setMore(false);
                      }}
                    >
                      {c.name}{" "}
                      <em>{c._id === ALL ? packages.length : packages.filter((p) => categoryOf(p)?._id === c._id).length}</em>
                    </button>
                  ))}
                </div>
              </div>
            )}
            {!loaded && <LoadingBlock />}
            <div className={clsx("tbl-wrap", !loaded && "!hidden")}>
              <table className="hp-tbl">
                <thead>
                  <tr>
                    <th scope="col">{t("Tên Server")}</th>
                    <th scope="col">CPU</th>
                    <th scope="col">GPU</th>
                    <th scope="col">RAM</th>
                    <th scope="col">SSD</th>
                    <th scope="col">OS</th>
                    <th scope="col" className="r">
                      {t("Giá theo giờ")}
                      <br />
                      {t("(VNĐ)")}
                    </th>
                    <th scope="col" className="r">
                      {t("Giá theo ngày")}
                      <br />
                      {t("(VNĐ)")}
                    </th>
                    <th scope="col" className="r">
                      {t("Giá theo tháng")}
                      <br />
                      {t("(VNĐ)")}
                    </th>
                    <th scope="col">
                      <span className="sr">{t("Thuê Ngay")}</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length ? (
                    rows.map((p) => (
                      <tr key={p._id}>
                        <td>
                          <div className="nm">
                            <b>{p.name}</b>
                            {tag(p)}
                          </div>
                        </td>
                        <td>{p.cpu}</td>
                        <td>{gpu(p)}</td>
                        <td>{p.ram}</td>
                        <td>{p.ssd}</td>
                        <td>{p.os}</td>
                        <td className="r num pz">{money(p.price_per_hour)}</td>
                        <td className="r num">{money(p.price_per_day)}</td>
                        <td className="r num">{money(p.price_per_month)}</td>
                        <td className="r">{rentBtn}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={10} className="!py-7 text-center text-muted">
                        {t("Không có cấu hình nào khớp bộ lọc.")}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className={clsx("hp-plist", !loaded && "!hidden")}>
              {rows.length ? (
                <>
                  {mobileRows.map((p) => (
                    <article key={p._id} className="hp-pc">
                      <div className="hp-pc-h">
                        <div>
                          <b>{p.name}</b>
                          {tag(p)}
                        </div>
                      </div>
                      <dl>
                        <dt>CPU</dt>
                        <dd>{p.cpu}</dd>
                        <dt>GPU</dt>
                        <dd>{gpu(p)}</dd>
                        <dt>RAM</dt>
                        <dd>{p.ram}</dd>
                        <dt>SSD</dt>
                        <dd>{p.ssd}</dd>
                        <dt>OS</dt>
                        <dd>{p.os}</dd>
                      </dl>
                      <div className="pr3 num">
                        <div>
                          <span>{t("Theo giờ")}</span>
                          <b>{money(p.price_per_hour)}</b>
                        </div>
                        <div>
                          <span>{t("Theo ngày")}</span>
                          <b>{money(p.price_per_day)}</b>
                        </div>
                        <div>
                          <span>{t("Theo tháng")}</span>
                          <b>{money(p.price_per_month)}</b>
                        </div>
                      </div>
                      {rentBtn}
                    </article>
                  ))}
                  {rows.length > MOBILE_ROWS && (
                    <button type="button" className="btn btn-ghost hp-pmore" onClick={() => setMore(!more)}>
                      {more ? t("Thu gọn") : `${t("Xem thêm")} ${rows.length - MOBILE_ROWS} ${t("cấu hình")}`}
                    </button>
                  )}
                </>
              ) : (
                <p className="note">{t("Không có cấu hình nào khớp bộ lọc.")}</p>
              )}
            </div>
            <div className="hp-pfoot">
              <span>
                <IcInfo className="i" />
                {t("Giá theo ngày = 24 giờ, giá theo tháng = 720 giờ. Giá cập nhật tự động theo trang Thuê máy tính.")}
              </span>
              <span>
                {t("Hiển thị")} {rows.length} {t("trong")} {packages.length} {t("cấu hình")}
              </span>
            </div>
          </section>

          {/* ---------- thuê theo nhu cầu ---------- */}
          <section className="hp-sec" aria-labelledby="hp-need-t">
            <div className="hp-head">
              <h2 id="hp-need-t">{t("Thuê Máy Theo Nhu Cầu")}</h2>
              <p>{t("Chọn đúng loại máy cho công việc của bạn, máy tạo tự động ngay sau khi thanh toán.")}</p>
            </div>
            <div className="hp-need">
              <Link className="hp-nd" to={rentTo} style={{ ["--glow" as string]: "var(--c-rd)" }}>
                <span className="ib">
                  <IcMonitor className="i" />
                </span>
                <h3>{t("Render video, Blender, Solidworks")}</h3>
                <p>
                  {t(
                    "Máy tính render video với RTX 3090, 4090. Thuê máy render Blender, thuê PC chạy Solidworks, 3ds Max theo giờ, không cần đầu tư phần cứng."
                  )}
                </p>
                <span className="from">{fromText(RE_RENDER)}</span>
              </Link>
              <Link className="hp-nd" to={rentTo} style={{ ["--glow" as string]: "var(--c-gl)" }}>
                <span className="ib">
                  <IcActivity className="i" />
                </span>
                <h3>{t("Treo giả lập, MMO")}</h3>
                <p>
                  {t(
                    "Thuê PC treo giả lập dual Xeon RAM lớn, treo game giả lập LDPlayer nhiều cửa sổ cùng lúc, chạy 24/7 không lo mất điện, mất mạng."
                  )}
                </p>
                <span className="from">{fromText(RE_GIALAP)}</span>
              </Link>
              <Link className="hp-nd" to={pageTo(PathnameUrl.CLOUD_RENTAL)} style={{ ["--glow" as string]: "var(--c-gm)" }}>
                <span className="ib">
                  <IcGamepad className="i" />
                </span>
                <h3>Cloud Gaming</h3>
                <p>
                  {t(
                    "Thuê máy chơi Cloud Gaming cấu hình cao, chơi game nặng trên laptop yếu hoặc điện thoại qua PCRender Play, mua gói giờ chơi dùng dần."
                  )}
                </p>
              </Link>
              <Link className="hp-nd" to={pageTo(PathnameUrl.VPS_RENTAL)} style={{ ["--glow" as string]: "var(--c-ai)" }}>
                <span className="ib">
                  <IcServer className="i" />
                </span>
                <h3>{t("Server GPU, VPS GPU")}</h3>
                <p>
                  {t(
                    "Thuê server GPU vật lý cho AI, render, đồ họa. Thuê VPS GPU giá rẻ theo tháng cho web, bot và chạy giả lập nhẹ."
                  )}
                </p>
              </Link>
            </div>
          </section>

          {/* ---------- Cloud VPS có gì tốt ---------- */}
          <section className="hp-why hp-sec" aria-labelledby="hp-why-t">
            <div className="hp-why-l">
              <h2 id="hp-why-t">{t("Cloud VPS Của PC Render Có Gì Tốt?")}</h2>
              <p>{t("Hệ thống cho thuê tự động 24/7, thanh toán linh hoạt theo giờ, ngày, tháng.")}</p>
              <div className="hp-cta">
                <Link className="btn btn-primary" to={rentTo}>
                  {t("Thuê máy ngay")}
                </Link>
              </div>
            </div>
            <div className="hp-why-g">
              <div className="hp-f">
                <div className="ib">
                  <IcCpu className="i" />
                </div>
                <h4>{t("PHẦN CỨNG MẠNH MẼ")}</h4>
                <p>
                  {t(
                    "Khi sử dụng dịch vụ tại PCRender, bạn có thể hoàn toàn yên tâm. Sử dụng CPU dual Xeon mới nhất, kết hợp cùng VGA 1080, 1080Ti, 3060… vô cùng mạnh mẽ."
                  )}
                </p>
              </div>
              <div className="hp-f">
                <div className="ib">
                  <IcFilter className="i" />
                </div>
                <h4>{t("NHIỀU LỰA CHỌN CẤU HÌNH")}</h4>
                <p>{t("PCRender cung cấp nhiều cấu hình tự chọn, từ VGA 1050Ti tới VGA 3060, 4090… vô cùng mạnh mẽ.")}</p>
              </div>
              <div className="hp-f">
                <div className="ib">
                  <IcFileText className="i" />
                </div>
                <h4>{t("THÔNG TIN MINH BẠCH, CHÍNH SÁCH RÕ RÀNG")}</h4>
                <p>
                  {t("Tất cả các thông tin liên quan đến")}{" "}
                  <Link to={PathnameUrl.TERMS}>
                    {t("điều khoản")}
                  </Link>{" "}
                  {t(
                    "và chính sách của Pc Render Cloud có nội dung thể hiện đầy đủ, chi tiết và được công khai. Khách hàng có thể an tâm tuyệt đối."
                  )}
                </p>
              </div>
              <div className="hp-f">
                <div className="ib">
                  <IcWallet className="i" />
                </div>
                <h4>{t("THANH TOÁN LINH HOẠT")}</h4>
                <p>
                  {t(
                    "Bạn có thể thanh toán theo giờ, ngày, tháng, năm, tùy vào nhu cầu sử dụng của bạn, hệ thống hoạt động 24/7 nên bạn có thể thuê máy bất kỳ thời gian nào."
                  )}
                </p>
              </div>
            </div>
          </section>

          {/* ---------- hệ điều hành ---------- */}
          <section className="hp-sec" aria-labelledby="hp-os-t">
            <div className="hp-head">
              <h2 id="hp-os-t">{t("Hệ Điều Hành Hỗ Trợ")}</h2>
              <p>
                {t("Chúng tôi hỗ trợ đầy đủ loại hệ điều hành Windows và Linux cho dịch vụ Cloud VPS.")}
                <br />
                {t("Cài đặt nhanh chóng, thuận tiện chỉ với một click chuột.")}
              </p>
            </div>
            <div className="hp-os">
              {OS_LIST.map(([name, sub, kind]) => (
                <div key={name} className="hp-osc">
                  <span className="logo-os">
                    <OsLogo kind={kind} />
                  </span>
                  <div>
                    <b>{name}</b>
                    <span>{t(sub)}</span>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* ---------- hỏi đáp ---------- */}
          <section className="hp-sec" aria-labelledby="hp-faq-t">
            <div className="hp-head">
              <h2 id="hp-faq-t">{t("Câu Hỏi Thường Gặp")}</h2>
            </div>
            <div className="hp-faq">
              {FAQ.map(([q, a], i) => (
                <details key={q} open={i === 0}>
                  <summary>{t(q)}</summary>
                  <p>{t(a)}</p>
                </details>
              ))}
            </div>
          </section>

          {/* ---------- dải kêu gọi ---------- */}
          <section className="hp-band" aria-labelledby="hp-band-t">
            <div>
              <h1 id="hp-band-t">{t("Cùng nhau, Trực tuyến.")}</h1>
              <p>{t("Chúng tôi ở đây giúp các bạn có thể làm việc online một cách hiệu quả nhất.")}</p>
            </div>
            <div className="hp-cta">
              <Link className="btn btn-w" to={rentTo}>
                {t("Thuê máy ngay")}
              </Link>
              <Link className="btn btn-o" to={pageTo(PathnameUrl.USER_GUIDE_MANAGEMENT)}>
                {t("Hướng dẫn sử dụng")}
              </Link>
            </div>
          </section>

          {/* ---------- chân trang: 3 cột cùng kiểu tiêu đề, các dòng cao bằng nhau để thẳng hàng ngang ---------- */}
          <footer className="hp-foot">
            <div className="hp-fcol">
              <h2>Pc Render</h2>
              <ul>
                <li>
                  <span className="ib">
                    <IcIdCard className="i" />
                  </span>
                  <span className="co">CÔNG TY TNHH CÔNG NGHỆ PCRENDER</span>
                </li>
                <li>
                  <span className="ib">
                    <IcMapPin className="i" />
                  </span>
                  <span>{t("Số 6 Tự Do 1, Phường Phú Thọ, TP Hồ Chí Minh")}</span>
                </li>
                <li>
                  <span className="ib">
                    <IcMail className="i" />
                  </span>
                  <a href="mailto:Support@pcrender.com">Support@pcrender.com</a>
                </li>
                <li>
                  <span className="ib">
                    <IcPhone className="i" />
                  </span>
                  <a className="num" href="tel:0833218888">
                    083.321.8888
                  </a>
                </li>
              </ul>
            </div>
            <div className="hp-fcol">
              <h2>{t("Thông tin")}</h2>
              <ul className="links">
                <li>
                  <Link to={PathnameUrl.INTRODUCE}>
                    {t("Giới Thiệu")}
                  </Link>
                </li>
                <li>
                  <Link to={PathnameUrl.CONTACT}>
                    {t("Liên Hệ")}
                  </Link>
                </li>
                <li>
                  <Link to={PathnameUrl.TERMS}>
                    {t("Điều Khoản")}
                  </Link>
                </li>
              </ul>
            </div>
            <div className="hp-fcol">
              <h4>{t("Theo Dõi Chúng Tôi")}</h4>
              <div className="hp-soc">
                <a href="https://www.facebook.com/pcrenderrdp" target="_blank" rel="noopener noreferrer" aria-label="Facebook PC Render">
                  <IcFacebook className="h-[17px] w-[17px]" />
                </a>
                <a href="https://www.youtube.com/@pcrender" target="_blank" rel="noopener noreferrer" aria-label="YouTube PC Render">
                  <IcYoutube className="h-[18px] w-[18px]" />
                </a>
                <a href="https://discord.gg/rBbqTrBD9w" target="_blank" rel="noopener noreferrer" aria-label="Discord PC Render">
                  <IcDiscord className="h-[18px] w-[18px]" />
                </a>
              </div>
            </div>
            <div className="hp-copy">
              <span>© {new Date().getFullYear()} CÔNG TY TNHH CÔNG NGHỆ PCRENDER</span>
              <span>pcrender.com</span>
            </div>
          </footer>
        </div>
      </main>
    </div>
  );
}

export default Home;

const selectMyInfo = (state: GlobalState) => state.myInfo;
